import { useCallback, useEffect, useRef, useState, useMemo } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import { useSessionState } from "./useSessionState"
import { SiteLayoutState, useSiteLayoutState } from "./useSiteLayoutState"

export const FWQUERYBROWSER_DEFAULT_PAGESIZE = 20

export type FwQueryResponse<TResponse> = {
  Items: TResponse[]
  PageNo: number
  PageSize: number
  TotalItems: number
  Sort: string
}

export type FwQueryFilter<T> = {
  field: keyof T | 'search'
  op: '=' | "<>" | "contains" | "does not contain" | "in" | "not in" | "starts with" | "ends with" | ">" | ">=" | "<" | "<"
  value: string
}

// Helper function to build the query URL
function buildQueryUrl<T>(
  baseurl: string,
  pageNo: number,
  pageSize: number,
  sort: string,
  filters: FwQueryFilter<T>[]
): string {
  const url = new URL(baseurl, window.location.origin);
  url.searchParams.append('pageno', pageNo.toString());
  url.searchParams.append('pagesize', pageSize.toString());
  
  // Add sort parameter if provided
  if (sort) {
    const sortParts = sort.split(' ');
    if (sortParts.length === 2) {
      const [field, direction] = sortParts;
      url.searchParams.append('sort', `${field} ${direction}`);
    } else {
      url.searchParams.append('sort', sort);
    }
  }
  
  // Add filters if provided
  if (filters && filters.length > 0) {
    url.searchParams.append('filter', JSON.stringify(filters));
  }
  
  return url.toString();
}

// Helper function to fetch data with authentication
async function fetchWithAuth<T>(url: string, sessionState: ReturnType<typeof useSessionState>, siteLayoutState: SiteLayoutState): Promise<FwQueryResponse<T>> {
  const headers: Record<string, string> = {};
  
  if (sessionState.get().loggedIn) {
    headers.Authorization = 'Bearer ' + sessionState.get().authToken;
  }
  
  const response = await fetch(url, {
    headers,
    method: 'GET'
  });

    if (response.status === 401) {
        await siteLayoutState.logoutAsync()
    }
  
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  return response.json();
}

// Interface for URL sync options
export interface UrlSyncOptions {
  enabled: boolean;
  routePath?: string;
  paramMapping?: {
    pageNo?: string;
    pageSize?: string;
    sort?: string;
    filters?: string;
  };
}

// Interface for initial state options
export interface InitialStateOptions {
  pageNo?: number;
  pageSize?: number;
  sort?: string;
  filters?: FwQueryFilter<any>[];
}

// Default URL parameter names
const DEFAULT_PARAM_MAPPING = {
  pageNo: 'pageno',
  pageSize: 'pagesize',
  sort: 'sort',
  filters: 'filters'
};

/**
 * React hook to track the state of GET endpoint that returns a FwQueryResponse<T>.
 * This provides functions to page through the data, change the filters, and get reactive changes
 * to the items and other properties like whether various buttons are disabled.
 * 
 * Uses Tanstack React Query for efficient data fetching and caching.
 * 
 * @param baseurl The base URL for the API endpoint
 * @param urlSync Optional configuration for URL synchronization
 * @param initialState Optional initial state values
 * @returns FwQueryState<T> object with data and functions
 */
export function useFwQuery<T>(
  baseurl: string, 
  urlSync: UrlSyncOptions = { enabled: true },
  initialState?: InitialStateOptions
): FwQueryState<T> {
  // console.log('🚀 useFwQuery initializing with:', {
  //   baseurl,
  //   urlSync,
  //   initialState,
  //   filters: initialState?.filters
    // });
  const siteLayoutState = useSiteLayoutState();

  
  // Configure URL synchronization
  const syncEnabled = urlSync.enabled;
  const paramMapping = urlSync.paramMapping || DEFAULT_PARAM_MAPPING;
  
  // Get router hooks if URL sync is enabled
  const sessionState = useSessionState();
  
  let [searchParams, setSearchParams] = useSearchParams();
  
  // Always read from URL for initialization, regardless of sync setting
  let pagenoUrl = 1;
  let pagesizeUrl = FWQUERYBROWSER_DEFAULT_PAGESIZE;
  let sortUrl = '';
  let filtersUrl: FwQueryFilter<T>[] = [];
  
  try {
    pagenoUrl = parseInt(searchParams.get('pageno') ?? pagenoUrl.toString());
  } catch {}
  try {
    pagesizeUrl = parseInt(searchParams.get('pagesize') ?? pagesizeUrl.toString());
  } catch {}
  sortUrl = searchParams.get('sort') ?? '';
  const filtersUrlJson = searchParams.get('filters') ?? '[]';
  try {
    filtersUrl = JSON.parse(filtersUrlJson);
  } catch {}
  
  // Compute initial state values - prioritize URL values when they exist
  const initialPageNo = (() => {
    // If URL has a page number, use it (regardless of sync setting)
    if (pagenoUrl !== 1) {
      return pagenoUrl;
    }
    // Otherwise use initial state or default
    return initialState?.pageNo ?? 1;
  })();
  
  const initialPageSize = (() => {
    // If URL has a page size, use it (regardless of sync setting)
    if (pagesizeUrl !== FWQUERYBROWSER_DEFAULT_PAGESIZE) {
      return pagesizeUrl;
    }
    // Otherwise use initial state or default
    return initialState?.pageSize ?? FWQUERYBROWSER_DEFAULT_PAGESIZE;
  })();
  
  const initialSort = (() => {
    // If URL has a sort, use it (regardless of sync setting)
    if (sortUrl) {
      return sortUrl;
    }
    // Otherwise use initial state or default
    return initialState?.sort ?? '';
  })();
  
  const initialFilters = (() => {
    // If URL has filters, use them (regardless of sync setting)
    if (filtersUrl.length > 0) {
      return filtersUrl;
    }
    // Otherwise use initial state or default
    return initialState?.filters ?? [];
  })();
  
  // console.log('🔧 useFwQuery initializing state:', { 
  //   initialStatePageNo: initialState?.pageNo, 
  //   pagenoUrl, 
  //   syncEnabled,
  //   finalPageNo: initialPageNo
  // });
  
  // Initialize state with computed initial values
  const [pageNo, setPageNo] = useState<number>(initialPageNo);
  const [pageSize, setPageSize] = useState<number>(initialPageSize);
  const [sort, setSort] = useState<string>(initialSort);
  const [filters, setFilters] = useState<FwQueryFilter<T>[]>(initialFilters);
  
  // console.log('🔧 useState initialized with:', { 
  //   initialPageNo, 
  //   pageNo, 
  //   initialFilters: initialFilters.length,
  //   filters: filters.length
  // });
  
  // Update state when initial values change (for when hook is recreated)
  useEffect(() => {
    // console.log('🔄 Updating state from initial values:', { 
    //   initialPageNo, 
    //   currentPageNo: pageNo,
    //   initialFilters: initialFilters.length,
    //   currentFilters: filters.length
    // });
    
    if (pageNo !== initialPageNo) {
      // console.log('📄 Updating pageNo from', pageNo, 'to', initialPageNo);
      setPageNo(initialPageNo);
    }
    if (pageSize !== initialPageSize) {
      setPageSize(initialPageSize);
    }
    if (sort !== initialSort) {
      setSort(initialSort);
    }
    if (JSON.stringify(filters) !== JSON.stringify(initialFilters)) {
      setFilters(initialFilters);
    }
  }, [initialPageNo, initialPageSize, initialSort, initialFilters, pageNo, pageSize, sort, filters]);
  
  // State to control if the query is ready for its initial fetch
  const [isQueryReadyForFirstFetch, setIsQueryReadyForFirstFetch] = useState<boolean>(true);
  
  // State for UI elements
  const [url, setUrl] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [firstBtnDisabled, setFirstBtnDisabled] = useState<boolean>(true);
  const [prevBtnDisabled, setPrevBtnDisabled] = useState<boolean>(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState<boolean>(true);
  const [lastBtnDisabled, setLastBtnDisabled] = useState<boolean>(true);
  const [pageNoBtnDisabled, setPageNoBtnDisabled] = useState<boolean>(true);
  const [pageSizeBtnDisabled, setPageSizeBtnDisabled] = useState<boolean>(true);
  
  // For tracking fetch status
  const fetchInProgress = useRef(false);
  
  // Manual loading state for refresh operations
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // Ref to track if we're updating URL params to prevent loops
  const isUpdatingUrlRef = useRef(false);
  
  // Get the React Query client
  const reactQueryClient = useQueryClient();
  
  // Create a unique query key based on current parameters
  const queryKey = useMemo<[string, number, number, string, string]>(() => [
    baseurl,
    pageNo,
    pageSize,
    sort,
    JSON.stringify(filters)
  ], [baseurl, pageNo, pageSize, sort, filters]);
  
  // Helper to try parsing filters from URL
  const tryParseFilters = useCallback((filtersStr: unknown): FwQueryFilter<T>[] => {
    if (typeof filtersStr !== 'string') return [];
    
    try {
      const parsed = JSON.parse(filtersStr);
      if (Array.isArray(parsed)) {
        return parsed as FwQueryFilter<T>[];
      }
    } catch (e) {
      console.error('Failed to parse filters from URL:', e);
    }
    
    return [];
  }, []);
  
  // Update URL if sync is enabled
  const updateUrl = useCallback(() => {
    // console.log('📝 updateUrl called:', { 
    //   syncEnabled, 
    //   isUpdatingUrlRef: isUpdatingUrlRef.current,
    //   pageNo,
    //   pageSize,
    //   sort,
    //   filters
    // });
    
    if (!syncEnabled || isUpdatingUrlRef.current) {
      // console.log('⏸️ Skipping updateUrl - not enabled or already updating');
      return;
    }
    
    isUpdatingUrlRef.current = true;
    try {
      const newSearchParams = new URLSearchParams(searchParams);
      
      // Page Number
      if (pageNo !== 1) {
        newSearchParams.set(paramMapping.pageNo || 'pageno', pageNo.toString());
      } else {
        newSearchParams.delete(paramMapping.pageNo || 'pageno');
      }

      // Page Size
      if (pageSize !== FWQUERYBROWSER_DEFAULT_PAGESIZE) {
        newSearchParams.set(paramMapping.pageSize || 'pagesize', pageSize.toString());
      } else {
        newSearchParams.delete(paramMapping.pageSize || 'pagesize');
      }

      // Sort
      if (sort) {
        newSearchParams.set(paramMapping.sort || 'sort', sort);
      } else {
        newSearchParams.delete(paramMapping.sort || 'sort');
      }

      // Filters
      if (paramMapping.filters !== undefined) {
        if (filters.length > 0) {
          newSearchParams.set(paramMapping.filters, JSON.stringify(filters));
        } else {
          newSearchParams.delete(paramMapping.filters);
        }
      }
      
      setSearchParams(newSearchParams);
    } finally {
      isUpdatingUrlRef.current = false;
    }
  }, [syncEnabled, pageNo, pageSize, sort, filters, paramMapping, searchParams, setSearchParams]);
  
  // Effect to synchronize state from URL when urlSearch changes or on initial load
  useEffect(() => {
    // console.log('🔄 URL sync effect running:', { 
    //   syncEnabled, 
    //   isUpdatingUrlRef: isUpdatingUrlRef.current,
    //   searchParams: Object.fromEntries(searchParams.entries())
    // });
    
    if (syncEnabled) {
      // If updateUrl is currently pushing state to the URL, avoid a feedback loop.
      if (isUpdatingUrlRef.current) {
        // console.log('⏸️ Skipping URL sync - currently updating URL');
        return;
      }

      const latestUrlPageNo = searchParams && paramMapping.pageNo && searchParams.get(paramMapping.pageNo)
                                ? Number(searchParams.get(paramMapping.pageNo)) : 1;
      const latestUrlPageSize = searchParams && paramMapping.pageSize && searchParams.get(paramMapping.pageSize)
                                  ? Number(searchParams.get(paramMapping.pageSize)) : FWQUERYBROWSER_DEFAULT_PAGESIZE;
      const latestUrlSort = searchParams && paramMapping.sort && searchParams.get(paramMapping.sort)
                              ? String(searchParams.get(paramMapping.sort)) : '';
      const latestUrlFilters = searchParams && paramMapping.filters !== undefined && searchParams.get(paramMapping.filters)
                                 ? tryParseFilters(searchParams.get(paramMapping.filters)) : [];

      // Update internal state only if it differs from what the URL indicates.
      if (pageNo !== latestUrlPageNo) {
        setPageNo(latestUrlPageNo);
      }
      if (pageSize !== latestUrlPageSize) {
        setPageSize(latestUrlPageSize);
      }
      if (sort !== latestUrlSort) {
        setSort(latestUrlSort);
      }
      if (JSON.stringify(filters) !== JSON.stringify(latestUrlFilters)) {
        setFilters(latestUrlFilters);
      }

      // After the first attempt to sync from URL (i.e., this effect runs),
      // the query is considered ready to make its first fetch with synced params.
      if (!isQueryReadyForFirstFetch) {
        // console.log('🚀 Enabling query after URL sync');
        setIsQueryReadyForFirstFetch(true);
      }
    }
  }, [
    syncEnabled, 
    searchParams, 
    paramMapping, 
    isQueryReadyForFirstFetch, 
    tryParseFilters, 
    pageNo, 
    pageSize, 
    sort, 
    filters
  ]);
  
  // Effect to update URL when state changes if sync is enabled
  useEffect(() => {
    if (isQueryReadyForFirstFetch) {
      updateUrl();
    }
  }, [isQueryReadyForFirstFetch, updateUrl]);
  
  // Helper for updating button states
  const updateButtonStates = useCallback((data?: FwQueryResponse<T>) => {
    const totalItems = data?.TotalItems || 0;
    const totalPages = Math.ceil(totalItems / pageSize) || 0;
    
    setFirstBtnDisabled(pageNo <= 1);
    setPrevBtnDisabled(pageNo <= 1);
    setNextBtnDisabled(pageNo >= totalPages);
    setLastBtnDisabled(pageNo >= totalPages);
    setPageNoBtnDisabled(false);
    setPageSizeBtnDisabled(false);
  }, [pageNo, pageSize]);

  // Use React Query for data fetching
  const {
    data: queryResult,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery<FwQueryResponse<T>, Error>({
    queryKey,
    queryFn: async () => {
      // console.log('🔍 React Query fetching data with key:', queryKey);
      
      if (!baseurl) {
        throw new Error('No base URL provided');
      }
      
      // Build the full query URL
      const fullUrl = buildQueryUrl(baseurl, pageNo, pageSize, sort, filters);
      // console.log('🔗 Full URL:', fullUrl);
      setUrl(fullUrl);
      
      // Fetch the data
      fetchInProgress.current = true;
      try {
        const response = await fetchWithAuth<T>(fullUrl, sessionState, siteLayoutState);
        
        if (response) {
          return response;
        }

        throw new Error('Unable to load data.');
      } finally {
        fetchInProgress.current = false;
      }
    },
    enabled: isQueryReadyForFirstFetch && !!baseurl && baseurl.trim() !== '',
    staleTime: 15 * 60 * 1000, // 15 minutes (increased for better back navigation)
    retry: (failureCount: number, error: Error) => {
      // Don't retry on 401/403 errors
      if (error.message.includes('Session expired') || error.message.includes('Unauthorized')) {
        return false;
      }
      // Only retry once for other errors
      return failureCount < 1;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnReconnect: false, // Don't refetch on reconnect if data is fresh
  });
  
  // console.log('🔍 Query state:', { isLoading, isError, hasData: !!queryResult });
  
  // Extract data from query result or provide defaults
  const data = queryResult;
  const items = data?.Items || [];
  
  // Preserve previous totalItems during loading to avoid showing 0
  const [previousTotalItems, setPreviousTotalItems] = useState<number>(0);
  
  // Update previous totalItems when we get new data
  useEffect(() => {
    if (data?.TotalItems !== undefined) {
      setPreviousTotalItems(data.TotalItems);
    }
  }, [data?.TotalItems]);
  
  // Use current data totalItems if available, otherwise preserve previous value during loading
  const totalItems = data?.TotalItems !== undefined ? data.TotalItems : (isLoading || isFetching ? previousTotalItems : 0);
  const totalPages = Math.ceil(totalItems / pageSize) || 0;
  
  // Set error message if there's an error
  useEffect(() => {
    if (isError && error) {
      setErrorMessage(error.message);
    } else {
      setErrorMessage('');
    }
  }, [isError, error]);
  
  // Update button states whenever data changes
  useEffect(() => {
    updateButtonStates(data);
  }, [data, updateButtonStates]);
  
  // Function to reset all state
  const reset = useCallback(() => {
    setPageNo(1);
    setPageSize(FWQUERYBROWSER_DEFAULT_PAGESIZE);
    setSort('');
    setFilters([]);
    setUrl('');
    setErrorMessage('');
  }, []);
  
  // Function to manually trigger refetch
  const loadAsync = useCallback(async (): Promise<void> => {
    if (fetchInProgress.current) {
      return;
    }
    
    try {
      // console.log('🔄 Refreshing data - setting manual loading state');
      // Set manual loading state to show loading dots
      setIsRefreshing(true);
      
      // Invalidate the query cache to force a fresh fetch
      await reactQueryClient.invalidateQueries({ queryKey });
      
      // Wait a moment for the invalidation to take effect, then refetch
      setTimeout(async () => {
        try {
          await refetch();
        } finally {
          setIsRefreshing(false);
        }
      }, 100);
      
    } catch (error) {
      console.error('Error refreshing data:', error);
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred');
      setIsRefreshing(false);
    }
  }, [refetch, reactQueryClient, queryKey]);
  
  // Navigation functions
  const firstPageAsync = useCallback(async (): Promise<void> => {
    if (!firstBtnDisabled && pageNo > 1) {
      setPageNo(1);
    }
  }, [firstBtnDisabled, pageNo]);

  const prevPageAsync = useCallback(async (): Promise<void> => {
    if (!prevBtnDisabled && pageNo > 1) {
      setPageNo(prev => prev - 1);
    }
  }, [prevBtnDisabled, pageNo]);

  const nextPageAsync = useCallback(async (): Promise<void> => {
    if (!nextBtnDisabled) {
      if (pageNo > totalPages) {
        setPageNo(totalPages);
      }
      else if (pageNo >= 1 && pageNo < totalPages) {
        setPageNo(prev => prev + 1);
      }
      else if (pageNo < 1) {
        setPageNo(1);
      }
    }
  }, [nextBtnDisabled, pageNo, totalPages]);

  const lastPageAsync = useCallback(async (): Promise<void> => {
    if (!lastBtnDisabled && pageNo < totalPages) {
      setPageNo(totalPages);
    }
  }, [lastBtnDisabled, pageNo, totalPages]);

  const setPageNoAsync = useCallback(async (newPageNo: number, overrideAllowSetPageNo: boolean = false): Promise<void> => {
    if ((!pageNoBtnDisabled || overrideAllowSetPageNo) && newPageNo !== pageNo) {
      if (newPageNo === 1) {
        setPageNo(newPageNo);
      }
      else if (newPageNo > 1 && data) {
        if (newPageNo >= 1 && newPageNo <= totalPages) {
          setPageNo(newPageNo);
        }
      }
    }
  }, [pageNoBtnDisabled, pageNo, data, totalPages]);

  const setPageSizeAsync = useCallback(async (newPageSize: number): Promise<void> => {
    if (!pageSizeBtnDisabled && newPageSize !== pageSize) {
      setPageNo(1);
      setPageSize(newPageSize);
    }
  }, [pageSizeBtnDisabled, pageSize]);

  // Filter and sort functions
  const setFiltersAsync = useCallback(async (newFilters: FwQueryFilter<T>[]): Promise<void> => {
    // Update filters and reset to page 1
    setFilters(newFilters);
    setPageNo(1);
    
    // Invalidate React Query cache for this query to force a refetch
    const cacheKey = [baseurl, 1, pageSize, sort, JSON.stringify(newFilters)];
    reactQueryClient.invalidateQueries({ queryKey: cacheKey });
  }, [baseurl, pageSize, sort, reactQueryClient]);

  const setSortAsync = useCallback(async (newSort: string): Promise<void> => {
    // Parse the sort string to extract field and direction
    const sortParts = newSort.split(' ');
    
    if (sortParts.length === 2) {
      // Valid sort format
    } else {
      console.warn('Invalid sort string format:', newSort);
      return; // Don't proceed with invalid format
    }
    
    // Update sort state
    setSort(newSort);
    
    // Invalidate React Query cache for this query to force a refetch
    const cacheKey = [baseurl, pageNo, pageSize, newSort, JSON.stringify(filters)];
    reactQueryClient.invalidateQueries({ queryKey: cacheKey });
  }, [baseurl, pageNo, pageSize, filters, reactQueryClient]);

  // console.log('🔧 Returning useFwQuery with pageNo:', pageNo, 'initialPageNo:', initialPageNo);

  // Calculate loading state - show loading when fetching or refreshing
  const isPendingValue = isLoading || isRefreshing;
  
  // Log loading state for debugging
  // console.log('🔍 Loading state:', {
  //   isPending: isPendingValue,
  //   isLoading,
  //   isFetching,
  //   hasCachedData: !!queryResult,
  //   isRefreshing
  // });

  return {
    // Props
    pageNo,
    pageSize,
    filters,
    sort,
    isPending: isPendingValue, // Show loading when fetching or refreshing
    errorMessage,
    items,
    totalItems,
    totalPages,
    url,
    firstBtnDisabled,
    prevBtnDisabled,
    nextBtnDisabled,
    lastBtnDisabled,
    pageNoBtnDisabled,
    pageSizeBtnDisabled,

    // Functions
    reset,
    loadAsync,
    firstPageAsync,
    prevPageAsync,
    nextPageAsync,
    lastPageAsync,
    setPageNoAsync,
    setPageSizeAsync,
    setFiltersAsync,
    setSortAsync
  };
}

export type FwQueryState<T> = {
  // Props
  pageNo: number
  pageSize: number
  filters: FwQueryFilter<T>[]
  sort: string
  isPending: boolean
  errorMessage: string
  items: T[]
  totalItems: number
  totalPages: number
  url: string
  firstBtnDisabled: boolean
  prevBtnDisabled: boolean
  nextBtnDisabled: boolean
  lastBtnDisabled: boolean
  pageNoBtnDisabled: boolean
  pageSizeBtnDisabled: boolean

  // Functions
  reset: () => void
  loadAsync: () => Promise<void>
  firstPageAsync: () => Promise<void>
  prevPageAsync: () => Promise<void>
  nextPageAsync: () => Promise<void>
  lastPageAsync: () => Promise<void>
  setPageNoAsync: (pageNo: number, overrideAllowSetPageNo?: boolean) => Promise<void>
  setPageSizeAsync: (pageSize: number) => Promise<void>
  setFiltersAsync: (filters: FwQueryFilter<T>[]) => Promise<void>
  setSortAsync: (sort: string) => Promise<void>
} 